import { FilterControllerContext, FilterControllerT } from '../filter-controller';
import { FilterData, FilterTag } from '../filter-data';

import * as Utils from '../../utils';

/**
 * For filters that can support various units, this interface defines a single unit.
 */
export interface RangeUnit {
  /**
   * Internal id of the unit.
   */
  id: string;

  /**
   * Label to display on the filter UI for the unit.
   */
  label: string;
}

/**
 * Defines the application-configured parameters for a range filter.
 */
export interface RangeFilterParams {
  /**
   * Defines the available units for a range length.
   */
  units?: RangeUnit[];

  /**
   * Placeholder text for min value (default: 'Min').
   */
  minHint?: string;

  /**
   * Placeholder text for max value (default: 'Max').
   */
  maxHint?: string;
}

/**
 * Defines user-configured data for a range filter.
 */
export class RangeFilterData implements FilterData {
  /**
   * Defines the minimum value.
   */
  minValue?: number;

  /**
   * Defines the maximum value.
   */
  maxValue?: number;

  /**
   * Defines the units of the range.
   */
  unitId?: string;

  /** FilterData implementation */

  clear(): void {
    this.minValue = undefined;
    this.maxValue = undefined;
    this.unitId = undefined;
  }

  copyFrom(data: RangeFilterData): void {
    this.minValue = data?.minValue;
    this.maxValue = data?.maxValue;
    this.unitId = data?.unitId;
  }

  getTags(filterParams: RangeFilterParams): FilterTag[] {
    let description = '';

    if (this.minValue !== undefined && this.maxValue !== undefined) {
      description = `${this.minValue} - ${this.maxValue}`;
    } else if (this.minValue !== undefined) {
      description = `>= ${this.minValue}`;
    } else if (this.maxValue !== undefined) {
      description = `<= ${this.maxValue}`;
    } else {
      return [];
    }

    const unit = filterParams.units?.find(value => value.id === this.unitId);
    if (unit) {
      description = description.concat(' ', unit.label);
    }

    return [
      {
        description: description,
        remove: () => {
          if (this.hasFilter()) {
            const newFilterData = new RangeFilterData();
            newFilterData.copyFrom(this);
            newFilterData.minValue = undefined;
            newFilterData.maxValue = undefined;
            newFilterData.unitId = undefined;
            return newFilterData;
          } else {
            return this;
          }
        }
      }
    ];
  }

  hasFilter(): boolean {
    return Utils.areAnyValid(this.minValue, this.maxValue);
  }
}

export class RangeFilterController extends FilterControllerT<RangeFilterData, RangeFilterParams> {
  constructor(context: FilterControllerContext) {
    super(context, () => {
      return new RangeFilterData();
    });

    this.initialFocusElementName = 'min-value';

    this.errors = {};
    this.state = {};
  }

  getTemplateContext(): any {
    const context = super.getTemplateContext();
    context.errors = this.errors;
    context.state = this.state;
    context.onModelChange = () => {
      this.evaluateHasRange();
      if (!this.state.hasRange) {
        this.localData.unitId = undefined;
      }
    };
    return context;
  }

  onActivate(isActive: boolean): void {
    super.onActivate(isActive);

    if (isActive) {
      // Make sure the selection is fully visible.
      this.ensureInView('unit-wrapper', this.localData.unitId + '-wrapper');
    }
  }

  onClear(): void {
    super.onClear();
    this.errors.values = false;
    this.state.hasRange = false;
  }

  canApply(): boolean {
    const hadValueError = this.errors.values;
    let returnValue = true;

    this.errors.values = false;

    // If both min and max are supplied, then min should be <= max.
    if (Utils.isValid(this.localData.minValue) && Utils.isValid(this.localData.maxValue)) {
      if (this.localData.minValue > this.localData.maxValue) {
        this.errors.values = true;
        returnValue = false;
      }
    }
    // If either are supplied, then we need units if a units collection is supplied.
    if (Utils.isValid(this.localData.minValue) || Utils.isValid(this.localData.maxValue)) {
      if (this.sourceParams?.units?.length && this.localData.unitId === undefined) {
        returnValue = false;
      }
    }

    if (hadValueError !== this.errors.values) {
      this.changeDetectorRef.detectChanges();
    }

    return returnValue;
  }

  protected evaluateLocalData(): boolean {
    // The values can be null if the user had data but cleared out the fields.
    if (this.localData.minValue === null) {
      this.localData.minValue = undefined;
    }
    if (this.localData.maxValue === null) {
      this.localData.maxValue = undefined;
    }

    return (
      this.localData.minValue !== this.sourceData?.minValue ||
      this.localData.maxValue !== this.sourceData?.maxValue ||
      this.localData.unitId !== this.sourceData?.unitId
    );
  }

  protected onInit(): void {
    super.onInit();
    this.errors.values = false;
    this.evaluateHasRange();
  }

  private evaluateHasRange(): void {
    this.state.hasRange = Utils.isValid(this.localData.minValue) || Utils.isValid(this.localData.maxValue);
  }

  private errors: {
    values?: boolean;
  };

  private state: {
    hasRange?: boolean;
  };
}
